import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component - Manages meta tags, Open Graph, Twitter Cards, and structured data
 * 
 * @param {string} title - Page title (will append " | NoteForge")
 * @param {string} description - Page description for search results
 * @param {string} keywords - Comma-separated keywords
 * @param {string} canonical - Canonical URL (defaults to current page)
 * @param {string} ogType - Open Graph type (website, article, etc.)
 * @param {string} ogImage - Social media share image URL
 * @param {object} structuredData - JSON-LD structured data
 */
const SEO = ({
  title = 'AI-Powered Note Taking & Transcription',
  description = 'Transform your meetings, lectures, and conversations into organized, searchable notes with NoteForge. AI-powered transcription, smart summaries, and real-time collaboration.',
  keywords = 'noteforge, noteforge.ai, noteforge ai, note taking app, AI transcription, meeting notes, voice to text, lecture notes, AI note taker, transcription app, meeting recorder, voice notes, study notes, auto text conversation, voice recorder conversation, speech to text, voice transcription, automatic note taking, meeting transcription, lecture recording, conversation recorder, audio to text converter, real-time transcription, AI meeting assistant, smart notes',
  canonical = '',
  ogType = 'website',
  ogImage = '/Images/noteforge-og-image.png',
  structuredData = null,
  noindex = false,
}) => {
  const siteUrl = 'https://noteforge.ai';
  const canonicalUrl = canonical || `${siteUrl}${window.location.pathname}`;
  const fullTitle = title.includes('NoteForge') ? title : `${title} | NoteForge`;
  const imageUrl = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="NoteForge" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={imageUrl} />

      {/* Additional Meta Tags */}
      <meta name="author" content="NoteForge" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="language" content="English" />

      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
